
import React, { useState, useEffect, useMemo } from 'react';
import { useAppContext } from '../App.jsx';
import { ArrowLeft, Eye, MessageSquare, Filter, List } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ProposalDetailModal from '../components/chat/ProposalDetailModal.jsx';
import AuthLoadingScreen from './AuthLoadingScreen.jsx';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import BottomNav from '../components/common/BottomNav.jsx';
import { CHATS_COLLECTION } from '../config/constants';

const ProposalCard = ({ proposal, onViewItems, onGoToChat, activeRole }) => {
    const statusConfig = {
        pending: { label: 'Aguardando', color: 'bg-yellow-500' },
        accepted: { label: 'Aceito', color: 'bg-green-500' },
        rejected: { label: 'Recusado', color: 'bg-red-500' },
        expired: { label: 'Expirado', color: 'bg-gray-400' },
    };
    const currentStatus = statusConfig[proposal.status] || { label: proposal.status, color: 'bg-gray-700' };

    const otherPartyName = activeRole === 'client'
        ? proposal.serviDetails?.companyName || proposal.serviDetails?.name
        : proposal.clientDetails?.name;

    return (
        <div className="bg-white p-4 rounded-xl shadow-md border-l-4 border-blue-500 text-left">
            <div className="flex justify-between items-start">
                <div className="min-w-0 flex-1">
                    <p className="font-bold text-gray-800 truncate">Proposta de: {otherPartyName || 'Usuário'}</p>
                    <p className="text-[10px] text-gray-500 uppercase font-black mt-1">Serviço: {proposal.items?.[0]?.description || 'Geral'}</p>
                </div>
                <span className={`text-[10px] font-black uppercase text-white px-2 py-1 rounded-full shrink-0 ml-2 ${currentStatus.color}`}>
                    {currentStatus.label}
                </span>
            </div>

            <div className="mt-4 pt-3 border-t flex justify-between items-center">
                <div className="flex space-x-2">
                    <button onClick={onViewItems} className="flex items-center text-[10px] bg-gray-100 text-gray-700 font-black uppercase px-3 py-1.5 rounded-lg">DETALHES</button>
                    <button onClick={onGoToChat} className="flex items-center text-[10px] bg-blue-600 text-white font-black uppercase px-3 py-1.5 rounded-lg shadow-md shadow-blue-100">IR PARA CHAT</button>
                </div>
                <p className="text-base font-black text-gray-900 tracking-tighter">R$ {proposal.total.toFixed(2)}</p>
            </div>
        </div>
    );
}

const ProposalsScreen = () => {
    const { user, activeRole, db } = useAppContext();
    const [proposals, setProposals] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedProposal, setSelectedProposal] = useState(null);
    const navigate = useNavigate();
    const [activeFilter, setActiveFilter] = useState('Todos');
    const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);

    useEffect(() => {
        if (!user || !activeRole) { setIsLoading(false); return; }

        const fetchAndFilterProposals = async () => {
            try {
                const functions = getFunctions(undefined, 'southamerica-east1');
                const getProposalsFunction = httpsCallable(functions, 'getProposals');
                const result = await getProposalsFunction({ role: activeRole });

                if (result.data?.proposals) {
                    const rawProposals = result.data.proposals;
                    const terminalStatus = ['Finalizado', 'Cancelado', 'Pago', 'Concluído', 'Recusado'];

                    // FILTRO NO FRONTEND: Verifica o status do chat em tempo real
                    const filtered = await Promise.all(rawProposals.map(async (p) => {
                        if (!p.chatId) return null;
                        const chatSnap = await getDoc(doc(db, CHATS_COLLECTION, p.chatId));
                        if (chatSnap.exists() && !terminalStatus.includes(chatSnap.data().status)) {
                            return p;
                        }
                        return null;
                    }));

                    setProposals(filtered.filter(p => p !== null));
                }
            } catch (error) {
                console.error("Erro ao buscar propostas:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAndFilterProposals();
    }, [user, activeRole, db]);

    const filteredProposals = useMemo(() => {
        if (activeFilter === 'Todos') return proposals;
        return proposals.filter(p => p.status === activeFilter);
    }, [proposals, activeFilter]);

    if (isLoading) return <AuthLoadingScreen message="Sincronizando orçamentos..." />;

    const filterOptions = [
        { label: 'Todos', value: 'Todos' },
        { label: 'Aguardando', value: 'pending' },
        { label: 'Aceitos', value: 'accepted' },
        { label: 'Recusados', value: 'rejected' },
    ];

    return (
        <div className="p-4 h-full flex flex-col bg-gray-50">
             <div className="flex items-center mb-4 text-left">
                <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-200 mr-2"><ArrowLeft size={24} /></button>
                <h2 className="text-xl font-bold text-gray-800 uppercase tracking-tighter">Meus Orçamentos</h2>
            </div>

            <div className="relative mb-4 flex justify-between items-center px-1">
                <button onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)} className="flex items-center space-x-2 px-4 py-2 bg-white border-2 border-gray-100 rounded-xl shadow-sm">
                    <Filter className="w-4 h-4 text-blue-600" />
                    <span className="text-[10px] font-black uppercase text-gray-600">{activeFilter}</span>
                </button>
                {isFilterMenuOpen && (
                    <div className="absolute left-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-2xl z-20 border p-2">
                        {filterOptions.map(o => (
                            <button key={o.value} onClick={() => { setActiveFilter(o.value); setIsFilterMenuOpen(false); }} className={`w-full text-left px-4 py-3 text-xs font-bold rounded-xl ${activeFilter === o.value ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>{o.label}</button>
                        ))}
                    </div>
                )}
            </div>

            <div className="space-y-3 flex-grow overflow-y-auto pb-24">
                {filteredProposals.length > 0 ? (
                    filteredProposals.map(p => (
                        <ProposalCard
                            key={p.id}
                            proposal={p}
                            activeRole={activeRole}
                            onViewItems={() => setSelectedProposal(p)}
                            onGoToChat={() => navigate(activeRole === 'servi' ? `/servi/chat/${p.chatId}` : `/chat/${p.chatId}`)}
                        />
                    ))
                ) : (
                    <div className="text-center py-20 bg-white rounded-[2rem] border-2 border-dashed border-gray-100"><List size={40} className="text-gray-200 mx-auto mb-3" /><p className="font-black text-gray-400 uppercase text-xs">Nenhum registro</p></div>
                )}
            </div>
            {selectedProposal && <ProposalDetailModal proposal={selectedProposal} onClose={() => setSelectedProposal(null)} />}
            <BottomNav />
        </div>
    );
};

export default ProposalsScreen;
